import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import {
  JoinAdmissionStatus,
  type JoinAdmissionUxDisposition,
} from "./admission-status.js";
import { detectBrowserCapabilities } from "./capabilities.js";
import {
  captureDisplay,
  createSyntheticCanvasCapture,
  disposeCapture,
} from "./capture.js";
import {
  ClientController,
  shouldShowDisplayShareRequest,
  type ClientViewState,
} from "./client-controller.js";
import { connectionBannerMessage } from "./connection-guidance.js";
import {
  requireValidDisplayName,
  validateDisplayName,
} from "./display-name.js";
import {
  buildJoinDeepLink,
  discoveryFromCode,
  discoveryFromDeepLink,
  discoveryErrorPresentation,
  type HostDiscovery,
} from "./discovery.js";
import {
  WEB_DEPLOYMENT_CONFIG,
  WEB_ROOM_DIRECTORY_ENABLED,
} from "./deployment-config.js";
import { IdentityStore } from "./identity.js";
import {
  ChangeBoardIcon,
  initialJoinDisplayName,
  LnaPromptCallout,
  LnaRecoveryGuidance,
  shouldUseSingleInputFallback,
} from "./join-ui.js";
import {
  RoomListLanding,
  SelectedRoomProof,
  type RoomDirectoryView,
  type SelectedRoomContext,
} from "./room-list-ui.js";
import { discoveryApiBaseUrl } from "./room-discovery.js";
import { SchoolIcon } from "./school-icon.js";
import {
  activeShareNeedsAutoFocus,
  AutoFocusToast,
  BoardContext,
  BottomBar,
  latchManualPlaneChoice,
  MaximizeToggle,
  mediaOverlayPresentation,
  PresenterNameplate,
  recoverySurfaceOwnsPresentation,
  RoomStageSkeleton,
  sessionPeerDisplayName,
  shouldShowGenericConnectionBanner,
  StatusRail,
  TransportPill,
  videoEmptyStateMessage,
  withPickerSelecting,
} from "./session-ui.js";
import { useNoticeAutoDismiss } from "./notice-dismiss.js";
import { strings } from "./strings.js";
import { visualAuditRouteRequested } from "./visual-audit-gate.js";
import type { Capabilities, StatsSnapshot } from "./wire.js";
import {
  WhiteboardView,
  type WhiteboardVisualAuditState,
} from "./whiteboard/WhiteboardView.js";

const E2E_ENABLED =
  import.meta.env.DEV || import.meta.env.VITE_KLASS_E2E === "1";
const VISUAL_AUDIT_ENABLED =
  E2E_ENABLED && visualAuditRouteRequested(window.location.search);
const DEFAULT_KOREAN_NAME = "김하늘 선생님";
const VISUAL_AUDIT_MARKER = "klass-share-uiux-visual-audit-v1";
const ROOM_DISCOVERY_API_BASE_URL = WEB_ROOM_DIRECTORY_ENABLED
  ? discoveryApiBaseUrl(WEB_DEPLOYMENT_CONFIG.discoveryApiBaseUrl)
  : undefined;

export interface KlassShareE2EVisualFixture {
  readonly marker: typeof VISUAL_AUDIT_MARKER;
  readonly admissionSecurity?: JoinAdmissionUxDisposition;
  readonly capabilities?: Capabilities;
  readonly discovery?: HostDiscovery;
  readonly discoveryError?: string;
  readonly displayName?: string;
  readonly displayNameError?: string;
  readonly hasResume?: boolean;
  readonly hostCode?: string;
  readonly online?: boolean;
  readonly pickerSelecting?: boolean;
  readonly roomDiscoveryApiBaseUrl?: string;
  readonly roomDirectory?: RoomDirectoryView;
  readonly syntheticRemoteVideo?: boolean;
  readonly view?: Omit<ClientViewState, "remoteStream">;
  readonly visiblePlane?: "video" | "whiteboard";
  readonly whiteboard?: WhiteboardVisualAuditState;
}

export interface KlassShareE2EApi {
  readonly version: 1;
  configure(options: { displayName?: string; h264Only?: boolean }): void;
  connect(options: {
    hostId: string;
    ip?: string;
    displayName?: string;
  }): Promise<void>;
  startSyntheticShare(): Promise<undefined>;
  failDirectLeg(): string | undefined;
  stopShare(): void;
  getState(): ClientViewState & {
    legs: ReturnType<ClientController["getLegs"]>;
  };
  getVideo(): {
    attached: boolean;
    readyState: number;
    width: number;
    height: number;
    paused: boolean;
    currentTime: number;
  };
  getStats(): Promise<StatsSnapshot[]>;
  setVisualState?(fixture: KlassShareE2EVisualFixture): void;
  state(): ClientViewState & { legs: ReturnType<ClientController["getLegs"]> };
  legs(): ReturnType<ClientController["getLegs"]>;
  stats(): Promise<StatsSnapshot[]>;
}

declare global {
  interface Window {
    __KLASS_SHARE_E2E__?: KlassShareE2EApi;
    __klassShareE2e?: KlassShareE2EApi;
  }
}

export function App(): React.JSX.Element {
  const initial = useMemo(readInitialDiscovery, []);
  const identityStore = useMemo(() => new IdentityStore(localStorage), []);
  const capabilities = useMemo(detectBrowserCapabilities, []);
  const [discovery, setDiscovery] = useState<HostDiscovery | undefined>(
    initial.discovery,
  );
  const [discoveryError, setDiscoveryError] = useState(initial.error);
  const [hostCode, setHostCode] = useState("");
  const [selectedRoom, setSelectedRoom] = useState<
    SelectedRoomContext | undefined
  >();
  const [displayName, setDisplayName] = useState(() =>
    initialJoinDisplayName(localStorage),
  );
  const [displayNameError, setDisplayNameError] = useState<
    string | undefined
  >();
  const [view, setView] = useState<ClientViewState | undefined>();
  const [online, setOnline] = useState(navigator.onLine);
  const [visiblePlane, setVisiblePlane] = useState<"video" | "whiteboard">(
    "whiteboard",
  );
  const [pickerSelecting, setPickerSelecting] = useState(false);
  const [joinSubmitting, setJoinSubmitting] = useState(false);
  const [videoMaximized, setVideoMaximized] = useState(false);
  const [admissionSecurity, setAdmissionSecurity] = useState<
    JoinAdmissionUxDisposition | undefined
  >();
  const [visualCapabilities, setVisualCapabilities] = useState<Capabilities>();
  const [visualHasResume, setVisualHasResume] = useState<boolean>();
  const [visualRoomDiscoveryApiBaseUrl, setVisualRoomDiscoveryApiBaseUrl] =
    useState<string>();
  const [visualRoomDirectory, setVisualRoomDirectory] =
    useState<RoomDirectoryView>();
  const [visualWhiteboard, setVisualWhiteboard] =
    useState<WhiteboardVisualAuditState>();
  const controllerRef = useRef<ClientController | undefined>(undefined);
  const unsubscribeRef = useRef<(() => void) | undefined>(undefined);
  const videoRef = useRef<HTMLVideoElement>(null);
  const autoFocusedShareIdRef = useRef<string | undefined>(undefined);
  const visualStreamRef = useRef<MediaStream | undefined>(undefined);
  const priorViewRef = useRef<ClientViewState | undefined>(undefined);
  const e2eOverrides = useRef<{ displayName?: string; h264Only?: boolean }>({});

  useEffect(() => {
    if (visiblePlane !== "video") setVideoMaximized(false);
  }, [visiblePlane]);

  useEffect(() => {
    if (!videoMaximized) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setVideoMaximized(false);
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [videoMaximized]);

  // Auto-dismiss the share-failure notice chip after a beat; a fresh failure
  // (new message) restarts the countdown so it always shows for its full window.
  const captureNoticeDismissed = useNoticeAutoDismiss(view?.captureMessage);

  const startController = useCallback(
    (
      target: HostDiscovery,
      name: string,
      capabilityOverride?: Capabilities,
      e2e = false,
    ) => {
      const normalizedName = requireValidDisplayName(name);
      unsubscribeRef.current?.();
      controllerRef.current?.disconnect(false);
      localStorage.setItem("klass-share.display-name.v1", normalizedName);
      const controller = new ClientController({
        discovery: target,
        identityStore,
        displayName: normalizedName,
        capabilities: capabilityOverride ?? capabilities,
        appVersion: __APP_VERSION__,
        captureDisplay: () =>
          withPickerSelecting(captureDisplay, setPickerSelecting),
        ...(e2e ? { e2eSessionStorage: sessionStorage } : {}),
      });
      controllerRef.current = controller;
      unsubscribeRef.current = controller.subscribe(setView);
      controller.connect();
      return controller;
    },
    [capabilities, identityStore],
  );

  useEffect(() => {
    const onOnline = () => setOnline(navigator.onLine);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOnline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOnline);
    };
  }, []);

  useEffect(
    () => () => {
      if (visualStreamRef.current !== undefined)
        disposeCapture(visualStreamRef.current);
    },
    [],
  );

  useEffect(() => {
    const video = videoRef.current;
    if (video === null) return;
    video.srcObject = view?.remoteStream ?? null;
    if (view?.remoteStream !== undefined)
      void video.play().catch(() => undefined);
  }, [view?.remoteStream]);

  useEffect(() => {
    if (view === priorViewRef.current) return;
    priorViewRef.current = view;
    setJoinSubmitting(false);
  }, [view]);

  useEffect(() => {
    const share = view?.signaling.room?.share;
    if (
      share?.status !== "active" ||
      !activeShareNeedsAutoFocus({
        shareId: share.shareId,
        latchedShareId: autoFocusedShareIdRef.current,
        remoteReady: view?.remoteStream !== undefined,
      })
    )
      return;
    autoFocusedShareIdRef.current = share.shareId;
    setVisiblePlane("video");
  }, [view?.remoteStream, view?.signaling.room?.share]);

  const switchPlaneManually = useCallback(
    (plane: "video" | "whiteboard") => {
      autoFocusedShareIdRef.current = latchManualPlaneChoice(
        autoFocusedShareIdRef.current,
        view?.signaling.room?.share,
      );
      setVisiblePlane(plane);
    },
    [view?.signaling.room?.share],
  );

  const returnToLanding = useCallback((message?: string) => {
    autoFocusedShareIdRef.current = undefined;
    setPickerSelecting(false);
    setJoinSubmitting(false);
    unsubscribeRef.current?.();
    unsubscribeRef.current = undefined;
    controllerRef.current?.disconnect(false);
    controllerRef.current = undefined;
    setView(undefined);
    setDiscovery(undefined);
    setSelectedRoom(undefined);
    setDiscoveryError(message);
  }, []);

  useEffect(() => {
    if (view?.signaling.phase === "room-closed") {
      returnToLanding(strings.recovery.roomClosedLanding);
    }
  }, [returnToLanding, view?.signaling.phase]);

  useEffect(() => {
    if (!E2E_ENABLED) return;
    const api: KlassShareE2EApi = {
      version: 1,
      configure: (options) => {
        if (controllerRef.current !== undefined)
          throw new Error("configure must run before connect");
        e2eOverrides.current = { ...e2eOverrides.current, ...options };
      },
      connect: async (options) => {
        const target =
          options.ip === undefined
            ? discoveryFromCode(options.hostId)
            : discoveryFromDeepLink(
                buildJoinDeepLink(options.hostId, options.ip),
              );
        if (target === undefined) throw new Error("invalid discovery target");
        setDiscovery(target);
        const override = e2eOverrides.current;
        const testCapabilities = {
          ...capabilities,
          ...(override.h264Only === undefined
            ? {}
            : { h264Only: override.h264Only }),
        };
        const controller = startController(
          target,
          options.displayName ?? override.displayName ?? DEFAULT_KOREAN_NAME,
          testCapabilities,
          true,
        );
        await controller.waitForJoined(15_000);
      },
      startSyntheticShare: () =>
        requireController(controllerRef.current).startSyntheticShare(),
      failDirectLeg: () =>
        requireController(controllerRef.current).failDirectLeg(),
      stopShare: () => requireController(controllerRef.current).stopShare(),
      getState: () => {
        const controller = requireController(controllerRef.current);
        return { ...controller.state, legs: controller.getLegs() };
      },
      getVideo: () => {
        const video = videoRef.current;
        return {
          attached: video?.srcObject instanceof MediaStream,
          readyState: video?.readyState ?? 0,
          width: video?.videoWidth ?? 0,
          height: video?.videoHeight ?? 0,
          paused: video?.paused ?? true,
          currentTime: video?.currentTime ?? 0,
        };
      },
      getStats: () => requireController(controllerRef.current).collectStats(),
      state: () => {
        const controller = requireController(controllerRef.current);
        return { ...controller.state, legs: controller.getLegs() };
      },
      legs: () => requireController(controllerRef.current).getLegs(),
      stats: () => requireController(controllerRef.current).collectStats(),
      ...(VISUAL_AUDIT_ENABLED
        ? {
            setVisualState: (fixture: KlassShareE2EVisualFixture) => {
              if (fixture.marker !== VISUAL_AUDIT_MARKER)
                throw new Error("visual audit fixture marker mismatch");
              unsubscribeRef.current?.();
              unsubscribeRef.current = undefined;
              controllerRef.current?.disconnect(false);
              controllerRef.current = undefined;
              if (visualStreamRef.current !== undefined)
                disposeCapture(visualStreamRef.current);
              const visualStream = fixture.syntheticRemoteVideo
                ? createSyntheticCanvasCapture(
                    "KlassShare 화면 공유 · 시각 감사",
                  )
                : undefined;
              visualStreamRef.current = visualStream;
              const share = fixture.view?.signaling.room?.share;
              autoFocusedShareIdRef.current =
                share?.status === "active" ? share.shareId : undefined;
              setDiscovery(fixture.discovery);
              setDiscoveryError(fixture.discoveryError);
              setDisplayName(fixture.displayName ?? "");
              setDisplayNameError(fixture.displayNameError);
              setHostCode(fixture.hostCode ?? "");
              setOnline(fixture.online ?? true);
              setPickerSelecting(fixture.pickerSelecting ?? false);
              setAdmissionSecurity(fixture.admissionSecurity);
              setJoinSubmitting(false);
              setView(
                fixture.view === undefined
                  ? undefined
                  : {
                      ...fixture.view,
                      ...(visualStream === undefined
                        ? {}
                        : { remoteStream: visualStream }),
                    },
              );
              setVisiblePlane(fixture.visiblePlane ?? "whiteboard");
              setVisualCapabilities(fixture.capabilities);
              setVisualHasResume(fixture.hasResume);
              setVisualRoomDiscoveryApiBaseUrl(
                discoveryApiBaseUrl(fixture.roomDiscoveryApiBaseUrl),
              );
              setVisualRoomDirectory(fixture.roomDirectory);
              setVisualWhiteboard(fixture.whiteboard);
            },
          }
        : {}),
    };
    window.__KLASS_SHARE_E2E__ = api;
    window.__klassShareE2e = api;
    return () => {
      delete window.__KLASS_SHARE_E2E__;
      delete window.__klassShareE2e;
    };
  }, [capabilities, startController]);

  const chooseCode = (event: React.FormEvent) => {
    event.preventDefault();
    try {
      setDiscovery(discoveryFromCode(hostCode));
      setSelectedRoom(undefined);
      setDiscoveryError(undefined);
    } catch (error) {
      setDiscoveryError(
        discoveryErrorPresentation(error, strings.landing.invalidCodeFallback),
      );
    }
  };

  const join = (event: React.FormEvent) => {
    event.preventDefault();
    if (discovery === undefined) return;
    const name = validateDisplayName(displayName);
    if (!name.ok) {
      setDisplayNameError(name.message);
      return;
    }
    setDisplayName(name.value);
    setDisplayNameError(undefined);
    flushSync(() => setJoinSubmitting(true));
    startController(discovery, name.value);
  };

  const hasResume =
    visualHasResume ??
    (discovery === undefined
      ? false
      : identityStore.load(discovery.hostId).resume !== undefined);
  const phase = view?.signaling.phase;
  const singleInputFallback = shouldUseSingleInputFallback(
    visualCapabilities ?? capabilities,
  );
  const room = view?.signaling.room;
  const source = room?.peers.find(
    (peer) => peer.clientId === room.share.sourceClientId,
  );
  const presenterRecovering =
    room?.share.status === "active" &&
    source?.connection === "zombie" &&
    view?.remoteStream === undefined;
  const presenterName =
    room?.share.status === "active" && source !== undefined
      ? sessionPeerDisplayName(source)
      : undefined;
  const mediaOverlay = mediaOverlayPresentation({
    hostMediaRecovering: room?.hostMedia === "recovering",
    presenterRecovering,
    remoteTrackMuted: view?.remoteTrackMuted === true,
  });
  return (
    <div className="app-shell">
      <header
        className={`topbar${view === undefined ? "" : " topbar-session"}`}
      >
        <a className="brand" href="/" aria-label={strings.brand.homeAria}>
          <span className="brand-mark" aria-hidden="true">
            <SchoolIcon />
          </span>
          <span>{strings.brand.name}</span>
        </a>
        {view !== undefined && discovery !== undefined && (
          <div
            className="session-context"
            aria-label={strings.brand.currentSessionAria}
          >
            <BoardContext hostId={discovery.hostId} />
            {view.signaling.transportMode !== undefined && (
              <TransportPill mode={view.signaling.transportMode} />
            )}
          </div>
        )}
        <span className={`network-pill ${online ? "online" : "offline"}`}>
          {online ? strings.network.online : strings.network.offline}
        </span>
      </header>
      {!online && (
        <aside className="truth-banner" role="status">
          {strings.network.offlineDetail}
        </aside>
      )}
      <JoinAdmissionStatus disposition={admissionSecurity} />
      <main className={view === undefined ? undefined : "main-session"}>
        {discovery === undefined ? (
          <RoomListLanding
            apiBaseUrl={
              visualRoomDiscoveryApiBaseUrl ?? ROOM_DISCOVERY_API_BASE_URL
            }
            codeError={discoveryError}
            hostCode={hostCode}
            initialDirectory={visualRoomDirectory}
            onCodeChange={setHostCode}
            onCodeSubmit={chooseCode}
            onRoomSelected={(target, context) => {
              setDiscovery(target);
              setSelectedRoom(context);
              setDiscoveryError(undefined);
            }}
            singleInputFallback={singleInputFallback}
          />
        ) : view === undefined ? (
          <section
            className="card join-card join-checkpoint"
            aria-labelledby="profile-heading"
          >
            <div className="join-destination">
              <div>
                <p className="eyebrow">{strings.landing.eyebrow}</p>
                <h1 id="profile-heading">
                  <BoardDestination hostId={discovery.hostId} />
                </h1>
              </div>
              <button
                id="change-board"
                type="button"
                className="secondary change-board"
                aria-label={strings.profile.changeBoardAria}
                onClick={() => returnToLanding()}
              >
                <span className="control-icon-label" data-control-icon-label>
                  <ChangeBoardIcon />
                  <span className="control-label">
                    {strings.profile.changeBoard}
                  </span>
                </span>
              </button>
            </div>
            {selectedRoom !== undefined && (
              <SelectedRoomProof context={selectedRoom} />
            )}
            <form onSubmit={join} className="stack">
              <div className="field-group">
                <label htmlFor="display-name">
                  {strings.profile.displayNameLabel}
                </label>
                <input
                  id="display-name"
                  value={displayName}
                  onChange={(event) => {
                    setDisplayName(event.target.value);
                    setDisplayNameError(undefined);
                  }}
                  maxLength={256}
                  aria-invalid={displayNameError !== undefined}
                  aria-describedby={
                    displayNameError === undefined
                      ? undefined
                      : "display-name-error"
                  }
                  autoComplete="name"
                  placeholder={strings.profile.displayNamePlaceholder}
                />
              </div>
              {displayNameError !== undefined && (
                <p id="display-name-error" className="error-text" role="alert">
                  {displayNameError}
                </p>
              )}
              {hasResume && (
                <p className="resume-note">{strings.profile.resumeNote}</p>
              )}
              <button type="submit" aria-busy={joinSubmitting}>
                {strings.profile.join}
              </button>
            </form>
          </section>
        ) : (
          <section
            className="room-layout session-shell"
            data-session-shell
            aria-label={strings.brand.sessionAria}
          >
            <StatusRail>
              {phase === "lna-prompt-pending" ? (
                <LnaPromptCallout
                  capabilities={view.signaling.capabilities}
                  message={connectionBannerMessage(phase)}
                />
              ) : shouldShowGenericConnectionBanner(phase) ? (
                <ConnectionBanner
                  key={phase ?? "connecting"}
                  phase={phase ?? "connecting"}
                />
              ) : null}
              {phase === "lna-denied" && (
                <div className="recovery-card lna-recovery-card" role="alert">
                  <LnaRecoveryGuidance
                    capabilities={view.signaling.capabilities}
                  />
                  <button
                    type="button"
                    onClick={() =>
                      controllerRef.current?.retryAfterLnaRecovery()
                    }
                  >
                    {strings.lna.permissionConfirmed}
                  </button>
                </div>
              )}
              {phase === "manual-reconnect" && (
                <div className="recovery-card" role="alert">
                  <h2>{strings.recovery.supersededTitle}</h2>
                  <p>{strings.recovery.supersededDetail}</p>
                  <button
                    type="button"
                    onClick={() =>
                      controllerRef.current?.prepareFreshJoinAfterSupersede()
                    }
                  >
                    {strings.recovery.reconnectHere}
                  </button>
                </div>
              )}
              {phase === "terminal-error" && (
                <div className="recovery-card" role="alert">
                  <h2>{strings.recovery.terminalTitle}</h2>
                  <p>{terminalJoinMessage(view.signaling.lastError)}</p>
                  <button type="button" onClick={() => returnToLanding()}>
                    {strings.recovery.returnToCode}
                  </button>
                </div>
              )}
              {phase === "stopped" && (
                <div className="recovery-card" role="alert">
                  <h2>{strings.recovery.stoppedTitle}</h2>
                  <p>{strings.recovery.stoppedDetail}</p>
                  <button type="button" onClick={() => returnToLanding()}>
                    {strings.recovery.returnToCode}
                  </button>
                </div>
              )}
            </StatusRail>
            <div className="stage card" data-session-stage>
              <h1 className="visually-hidden">
                {room === undefined
                  ? strings.session.stageConnecting
                  : strings.session.stageReady}
              </h1>
              {room?.share.status === "active" && (
                <PresenterNameplate presenterName={presenterName} />
              )}
              <RoomStageSkeleton visible={room === undefined} />
              <div
                className={`video-frame ${
                  visiblePlane === "video" ? "" : "plane-hidden"
                }${videoMaximized ? " video-frame-max" : ""}`}
                data-plane-surface="video"
                data-video-maximized={videoMaximized ? "true" : undefined}
              >
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  aria-label={strings.session.sharedVideoAria}
                />
                <MaximizeToggle
                  maximized={videoMaximized}
                  onToggle={() => setVideoMaximized((value) => !value)}
                />
                {room !== undefined && view.remoteStream === undefined && (
                  <div className="empty-stage">
                    {videoEmptyStateMessage(view.signaling.transportMode)}
                  </div>
                )}
                {mediaOverlay !== undefined && (
                  <div
                    className={`media-overlay media-overlay-${mediaOverlay.kind}`}
                    data-media-overlay={mediaOverlay.kind}
                    role="status"
                  >
                    {mediaOverlay.message}
                  </div>
                )}
              </div>
              <div data-plane-surface="whiteboard">
                <WhiteboardView
                  session={controllerRef.current?.whiteboardSession}
                  visible={visiblePlane === "whiteboard"}
                  deviceClass={view.signaling.capabilities.deviceClass}
                  visualAudit={visualWhiteboard}
                />
              </div>
              {room?.share.status === "active" &&
                !recoverySurfaceOwnsPresentation(phase) && (
                  <AutoFocusToast
                    key={room.share.shareId}
                    presenterName={presenterName}
                    presenterIsHost={source?.clientId === "host"}
                    remoteReady={view.remoteStream !== undefined}
                  />
                )}
            </div>
            <BottomBar
              activePlane={visiblePlane}
              localCaptureActive={view.localCaptureActive}
              needsCaptureGesture={view.needsCaptureGesture}
              onLeave={() => returnToLanding()}
              onRequestShare={() => {
                setPickerSelecting(false);
                controllerRef.current?.requestDisplayShare();
              }}
              onStartCapture={() => {
                controllerRef.current?.startGrantedCapture();
              }}
              onStopShare={() => {
                setPickerSelecting(false);
                controllerRef.current?.stopShare();
              }}
              onSwitchPlane={switchPlaneManually}
              peers={room?.peers ?? []}
              pickerSelecting={pickerSelecting}
              presenterClientId={room?.share.sourceClientId}
              requestShareDisabled={phase !== "joined"}
              roomResolved={room !== undefined}
              selfClientId={view.signaling.clientId}
              shareState={{
                captureMessage: captureNoticeDismissed
                  ? undefined
                  : view.captureMessage,
                captureRequestPending: view.captureRequestPending,
                localCaptureActive: view.localCaptureActive,
                needsCaptureGesture: view.needsCaptureGesture,
              }}
              showShareRequest={shouldShowDisplayShareRequest(view)}
              verifiedHostId={discovery.hostId}
            />
          </section>
        )}
      </main>
    </div>
  );
}

function ConnectionBanner({
  phase,
}: {
  phase: ClientViewState["signaling"]["phase"];
}): React.JSX.Element {
  const spinning =
    phase === "connecting" ||
    phase === "reconnecting" ||
    phase === "reconnect-escalated";
  return (
    <div
      className={`connection-banner connection-banner-${phase}`}
      data-connection-phase={phase}
      role="status"
    >
      {spinning && <span className="connection-spinner" aria-hidden="true" />}
      <span className="connection-banner-copy">
        {connectionBannerMessage(phase)}
      </span>
    </div>
  );
}

function BoardDestination({ hostId }: { readonly hostId: string }) {
  const destination = strings.profile.destination(hostId);
  const hostIdOffset = destination.indexOf(hostId);
  if (hostIdOffset < 0) return destination;
  return (
    <>
      {destination.slice(0, hostIdOffset)}
      <span className="destination-code">{hostId}</span>
      <span className="destination-action">
        {destination.slice(hostIdOffset + hostId.length)}
      </span>
    </>
  );
}

function readInitialDiscovery(): { discovery?: HostDiscovery; error?: string } {
  try {
    return { discovery: discoveryFromDeepLink(window.location.href) };
  } catch (error) {
    return {
      error: discoveryErrorPresentation(
        error,
        strings.landing.invalidQrFallback,
      ),
    };
  }
}

function requireController(
  controller: ClientController | undefined,
): ClientController {
  if (controller === undefined) throw new Error("connect must complete first");
  return controller;
}

function terminalJoinMessage(
  error: ClientViewState["signaling"]["lastError"],
): string {
  switch (error?.code) {
    case "VERSION_MISMATCH":
      return strings.errors.terminal.versionMismatch;
    case "ROOM_FULL":
      return strings.errors.terminal.roomFull;
    case "RATE_LIMITED":
      return strings.errors.terminal.rateLimited;
    case "LICENSE_EXPIRED":
    case "LICENSE_INVALID":
      return strings.errors.terminal.licenseUnavailable;
    case "INVALID_PAYLOAD":
      return strings.errors.terminal.invalidPayload;
    default:
      return strings.errors.terminal.rejected;
  }
}
